// components/ProcessSection.js
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const ProcessSection = () => {
  const steps = [
    {
      number: "01",
      title: "Conecte seu Projeto",
      description: "Informe a URL do seu site e nossa plataforma analisará automaticamente sua estrutura."
    },
    {
      number: "02",
      title: "Personalização Inteligente",
      description: "Configure identidade visual, cores, ícone do aplicativo e logotipo em poucos cliques."
    },
    {
      number: "03",
      title: "Escolha seus Destinos",
      description: "Publique para Android, iOS ou ambos utilizando uma única configuração."
    },
    {
      number: "04",
      title: "Deploy Automatizado",
      description: "Nossa tecnologia cuida da geração, otimização e publicação do aplicativo nas lojas oficiais."
    }
  ];

  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % steps.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [steps.length]);

  return (
    <section id="process" className="py-24 bg-[#050505] relative overflow-hidden">
      {/* Elementos de background abstratos de IA */}
      <div className="absolute top-0 left-1/2 w-full max-w-4xl -translate-x-1/2 h-64 bg-blue-500/10 blur-[100px] pointer-events-none rounded-full"></div>
      
      <div className="container mx-auto px-4 max-w-6xl relative z-10">
        <div className="text-center mb-20">
          <h2 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight mb-4">
            Seu Aplicativo Pronto em Minutos
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg font-light leading-relaxed">
            Transformamos seu site em um aplicativo profissional através de um processo inteligente e automatizado.
          </p>
        </div>

        <div className="relative pt-10 pb-4">
          {/* Linha de conexão futurista - DESKTOP */}
          <div className="hidden md:flex absolute top-10 left-[10%] right-[10%] h-16 items-center pointer-events-none">
            <div className="w-full h-[1px] bg-white/10 relative">
              <motion.div 
                className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 via-purple-500 to-cyan-400 shadow-[0_0_15px_rgba(59,130,246,0.5)]"
                initial={{ width: 0 }}
                animate={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
                transition={{ duration: 1, ease: "easeInOut" }}
              />
            </div>
          </div>

          {/* Linha de conexão futurista - MOBILE */}
          <div className="md:hidden absolute top-[40px] bottom-[40px] left-[16px] w-10 flex justify-center pointer-events-none">
            <div className="h-full w-[1px] bg-white/10 relative">
              <motion.div 
                className="absolute top-0 left-0 w-full bg-gradient-to-b from-blue-500 via-purple-500 to-cyan-400 shadow-[0_0_15px_rgba(59,130,246,0.5)]"
                initial={{ height: 0 }}
                animate={{ height: `${(currentStep / (steps.length - 1)) * 100}%` }}
                transition={{ duration: 1, ease: "easeInOut" }}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-6 relative z-10">
            {steps.map((step, index) => (
              <div key={index} className="flex flex-col md:items-center items-start pl-20 md:pl-0 relative group">
                
                {/* Indicador Numérico Mobile (Posicionado à esquerda) */}
                <div className="md:hidden absolute left-4 top-0 w-10 h-10 rounded-full bg-[#0a0a0a] border border-white/10 flex items-center justify-center z-10">
                  <span className={`text-sm font-medium transition-colors duration-500 ${currentStep >= index ? 'text-white' : 'text-gray-600'}`}>{step.number}</span>
                  {currentStep === index && (
                    <div className="absolute inset-0 rounded-full border border-blue-500/50 shadow-[0_0_10px_rgba(59,130,246,0.5)] animate-pulse"></div>
                  )}
                </div>

                {/* Container do círculo Desktop */}
                <div className="hidden md:flex relative mb-8 w-16 h-16 shrink-0 items-center justify-center">
                  <div className={`absolute inset-0 rounded-full transition-all duration-500 ${
                    currentStep >= index 
                      ? 'bg-[#0a0a0a] border border-white/20 shadow-[0_0_20px_rgba(255,255,255,0.05)]' 
                      : 'bg-[#0a0a0a] border border-white/5'
                  }`}></div>
                  
                  <span className={`text-sm font-mono tracking-widest relative z-10 transition-colors duration-500 ${
                    currentStep >= index ? 'text-white' : 'text-gray-600'
                  }`}>
                    {step.number}
                  </span>

                  {currentStep === index && (
                    <div className="absolute inset-0 rounded-full border border-blue-500/30 shadow-[0_0_15px_rgba(59,130,246,0.3)] animate-pulse"></div>
                  )}
                </div>

                {/* Card de Conteúdo */}
                <div className={`text-left md:text-center w-full bg-white/[0.02] border border-white/5 p-6 rounded-2xl transition-all duration-500 h-full flex flex-col hover:bg-white/[0.04] ${currentStep === index ? 'ring-1 ring-white/10 bg-white/[0.04] transform -translate-y-2' : ''}`}>
                  <h3 className={`text-lg font-semibold mb-3 tracking-tight transition-colors duration-500 ${
                    currentStep >= index ? 'text-white' : 'text-gray-500'
                  }`}>
                    <span className="md:hidden mr-2 text-blue-400 font-mono text-sm opacity-50">{step.number} —</span>
                    {step.title}
                  </h3>
                  <p className="text-gray-400 font-light text-sm leading-relaxed flex-grow">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Indicadores Minimalistas Mobile */}
          <div className="mt-12 flex justify-center md:hidden">
            <div className="flex space-x-3">
              {steps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStep(index)}
                  className="p-2"
                >
                  <motion.div
                    className={`h-1 rounded-full ${currentStep === index ? 'bg-blue-500' : 'bg-white/10'}`}
                    animate={{ width: currentStep === index ? 32 : 12 }}
                    transition={{ duration: 0.3 }}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;